// Venue price/capacity come back from the API as pre-formatted strings
// (e.g. "INR 140.00/event", "300 guests") or the literal sentinel "N/A" —
// these helpers parse them defensively so a missing/unexpected value never
// breaks the page, it just falls back to a neutral display.

// Shared with the venue detail page (Pricing Options) and the RFP form
// (pricing_type select) — keep these two consumers in sync.
export const VENUE_PRICING_TYPE_LABELS = {
    per_hour: 'Per Hour',
    per_day: 'Per Day',
    per_event: 'Per Event',
    per_plate: 'Per Plate',
}

export const parseVenuePrice = (priceStr) => {
    const match = typeof priceStr === 'string' ? priceStr.match(/([\d,]+(?:\.\d+)?)\s*\/\s*(\w+)/) : null
    if (!match) return { value: 0, display: '—', unit: '' }
    const numeric = Number(match[1].replace(/,/g, ''))
    const unit = match[2]
    return {
        value: numeric,
        display: `₹${numeric.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`,
        unit: unit.charAt(0).toUpperCase() + unit.slice(1),
    }
}

export const parseVenueCapacity = (capacityStr) => {
    const match = typeof capacityStr === 'string' ? capacityStr.match(/[\d,]+/) : null
    return match ? Number(match[0].replace(/,/g, '')) : null
}

// Hybrid payments (2026-09): a vendor can mark an installment paid in cash
// from their own panel (we never call that endpoint — it's vendor-only),
// so an installment can go from pending -> paid without the user ever
// touching our checkout/verify flow. `payment_method` distinguishes how.
export const PAYMENT_METHOD_LABELS = {
    razorpay: 'Razorpay',
    stripe: 'Stripe',
    cash: 'Cash',
    offline: 'Cash',
}

// Normalizes one payment-schedule entry regardless of which endpoint it
// came from — confirm-booking-details's schedule has no per-item status at
// all ({step, title, amount, currency, due_text}), while booking-confirmation
// (confirmed live 2026-09-21, Stripe) uses {step, title, amount (string),
// currency, is_paid (boolean)} — no due_text, no payment_method/paid_at per
// item. One shape lets every consumer render either without knowing which
// source it got.
export const mapPaymentScheduleItem = (item = {}, index = 0) => ({
    step: item.step ?? index + 1,
    title: item.title || item.name || `Installment ${index + 1}`,
    amount: item.amount,
    currency: item.currency,
    dueText: item.due_text || '',
    status: item.status || null,
    isPaid: item.status === 'paid' || item.is_paid === true,
    paidAt: item.paid_at || null,
    paymentMethod: item.payment_method || null,
    paymentMethodLabel: item.payment_method ? (PAYMENT_METHOD_LABELS[item.payment_method] || item.payment_method) : null,
    paymentId: item.payment_id || null,
})

// booking-confirmation's schedule array key has a confirmed-live stray
// trailing space, `"payment_schedule "` — checked defensively alongside the
// normal key in case the backend fixes the typo later.
export const extractScheduleArray = (body) => {
    if (!body) return []
    const raw = body.payment_schedule ?? body['payment_schedule '] ?? []
    return Array.isArray(raw) ? raw : []
}

// Confirmed live 2026-09-21: neither GET /users/venues (list) nor GET
// /users/venues/{id} (detail — its own top-level `price` field, confirmed
// literally "N/A") return a usable pre-formatted `price` string — both (and
// `similar_properties`, nested in the detail response, also confirmed live)
// carry a `pricings` array instead ([{pricing_type, currency, price,
// weekend_price, holiday_price, ...}]). Every "one headline price" spot
// shows the `per_day` row specifically (per explicit user request — venue
// cards/hero price should read as a daily rate, not whichever type happens
// to be cheapest), falling back to the cheapest available row only if this
// venue has no `per_day` pricing at all. Exported so both the list card
// mapper below and the detail/RFP pages' hero price can share it. Falls
// back to the old string-parsing only if `pricings` is ever absent, e.g. a
// future endpoint that still uses it.
export const pickListingPrice = (venue) => {
    const rows = Array.isArray(venue.pricings)
        ? venue.pricings.filter((row) => row && row.price != null && !Number.isNaN(Number(row.price)))
        : []
    if (rows.length) {
        const perDay = rows.find((row) => row.pricing_type === 'per_day')
        const chosen = perDay || rows.reduce((min, row) => (Number(row.price) < Number(min.price) ? row : min), rows[0])
        const unit = (VENUE_PRICING_TYPE_LABELS[chosen.pricing_type] || chosen.pricing_type || '').replace(/^Per\s+/, '')
        const numeric = Number(chosen.price)
        return {
            value: numeric,
            display: `₹${numeric.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`,
            unit,
        }
    }
    return parseVenuePrice(venue.price)
}

// Shared shape used by both GET /users/venues (list) and the
// `similar_properties` array nested inside GET /users/venues/{id}.
export const mapVenueListItem = (venue, fallbackImage) => {
    const price = pickListingPrice(venue)
    return {
        id: venue.id,
        name: venue.name || 'Venue',
        venueType: venue.type || '',
        capacity: parseVenueCapacity(venue.capacity),
        images: venue.cover_photo ? [venue.cover_photo] : [fallbackImage],
        rating: venue.rating ?? '—',
        reviewsCount: venue.reviews_count ?? venue.total_reviews ?? null,
        priceValue: price.value,
        price: price.display,
        priceUnit: price.unit,
        location: venue.address || 'Location not specified',
        amenities: (venue.amenities || []).map((item) => (typeof item === 'string' ? item : item?.name)).filter(Boolean),
    }
}
